import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FormState, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, CircularProgress } from '@mui/material';
import {
  FormDateRangeInput,
  FormFileInput,
  FormSelectInput,
  FormTextInput,
  FormToggleSwitch,
} from '@/components';
import {
  getDateRangeConflicts,
  getDateRangeErrors,
} from '@/components/inputs/dateRangeValidation';
import { formatDate, timeAgo } from '@/utils';
import {
  TRACKING_ICON_ANCHORS,
  TRACKING_ICON_ANCHOR_LABELS,
  TRACKING_ICON_ANCHOR_DESCRIPTIONS,
  getTrackingIconAnchor,
  TrackingIcon,
  TrackingIconSlot,
} from '../models';
import { useGetTrackingIconAvailabilityQuery } from '../services/tracking-icons.service';
import {
  trackingIconBlockedRanges,
  trackingIconEnabledDefault,
} from '../utils/trackingIconAvailability';
import {
  TrackingIconFormMode,
  TrackingIconFormValues,
  trackingIconDefaultValues,
  trackingIconFormSchema,
  trackingIconPath,
} from '../models/TrackingIconForm';
import TrackingIconImage from './TrackingIconImage';
import TrackingIconMapPreview from './TrackingIconMapPreview';

interface TrackingIconFormProps {
  icon?: TrackingIcon;
  slot: TrackingIconSlot;
  mode: TrackingIconFormMode;
  title: string;
  isSaving: boolean;
  action: (data: TrackingIconFormValues) => Promise<void | string>;
  renderButtons: (
    submit: () => void,
    formState: FormState<TrackingIconFormValues>
  ) => ReactNode;
}

const TrackingIconForm = ({
  icon,
  slot,
  mode,
  title,
  isSaving,
  action,
  renderButtons,
}: TrackingIconFormProps) => {
  const isCreating = mode === 'create';
  const canChangeImage = isCreating || mode === 'image';
  const readOnly = mode === 'view' || mode === 'image';
  const availability = useGetTrackingIconAvailabilityQuery(slot, {
    skip: readOnly,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });
  const parsedAvailability = useMemo(() => {
    try {
      return {
        ranges: trackingIconBlockedRanges(
          availability.currentData ?? [],
          slot,
          icon?.id
        ),
        error: false,
      };
    } catch {
      return { ranges: [], error: true };
    }
  }, [availability.currentData, slot, icon?.id]);
  const availabilityFailed = !!availability.error || parsedAvailability.error;
  const availabilityPending =
    !readOnly &&
    (availability.isFetching ||
      (!availability.currentData && !availabilityFailed));
  const availabilityMessage = availabilityFailed
    ? 'No pudimos comprobar la disponibilidad. Reintentá para guardar el ícono habilitado.'
    : availabilityPending
      ? 'Comprobando disponibilidad…'
      : undefined;
  const existingDefault = trackingIconEnabledDefault(
    availability.currentData ?? [],
    slot,
    icon?.id
  );
  const defaultConflictMessage = existingDefault
    ? `«${existingDefault.name}» ya es el ícono predeterminado habilitado para ${slot === 'driver' ? 'el repartidor' : 'el destino'}.`
    : undefined;
  const {
    control,
    handleSubmit,
    watch,
    formState,
    trigger,
    setError,
    clearErrors,
    setValue,
    reset,
  } = useForm<TrackingIconFormValues>({
    defaultValues: trackingIconDefaultValues(icon),
    resolver: zodResolver(
      trackingIconFormSchema(
        mode,
        parsedAvailability.ranges,
        availabilityMessage,
        defaultConflictMessage
      )
    ),
    mode: 'onChange',
  });
  useEffect(() => {
    if (mode === 'view') reset(trackingIconDefaultValues(icon));
  }, [icon, mode, reset]);

  const image = watch('image');
  const dateFrom = watch('dateFrom');
  const dateTo = watch('dateTo');
  const enabled = watch('enabled');
  const isDefault = watch('isDefault');
  const anchor = watch('anchor');
  const [previewImage, setPreviewImage] = useState<{
    file: File;
    url: string;
  }>();
  const preview = previewImage?.file === image ? previewImage?.url : undefined;
  const previewUrl = image ? preview : icon?.imageUrl;
  const hasInteractedWithPeriod = !!(
    formState.dirtyFields.dateFrom ||
    formState.dirtyFields.dateTo ||
    formState.touchedFields.dateFrom ||
    formState.touchedFields.dateTo ||
    formState.submitCount > 0
  );
  const isInitialEmptyPeriod =
    !hasInteractedWithPeriod && dateFrom === null && dateTo === null;
  const hasInteractedWithConfiguration =
    hasInteractedWithPeriod ||
    !!formState.dirtyFields.isDefault ||
    !!formState.touchedFields.isDefault;
  const blockedRanges =
    enabled && !readOnly && !isDefault ? parsedAvailability.ranges : [];
  const conflicts = isDefault
    ? []
    : getDateRangeConflicts(
        { from: dateFrom, to: dateTo },
        parsedAvailability.ranges,
        true
      );
  const validPeriod =
    Object.keys(
      getDateRangeErrors(
        { from: dateFrom, to: dateTo },
        { withTime: true, allowOpenRange: true }
      )
    ).length === 0;

  useEffect(() => {
    // Revalidate changed settings when availability finishes refreshing.
    if (!readOnly && hasInteractedWithConfiguration) {
      void trigger(['dateFrom', 'dateTo', 'isDefault']);
    }
  }, [
    dateFrom,
    dateTo,
    enabled,
    isDefault,
    defaultConflictMessage,
    parsedAvailability,
    availabilityMessage,
    readOnly,
    hasInteractedWithConfiguration,
    trigger,
  ]);

  useEffect(() => {
    if (!image) {
      setPreviewImage(undefined);
      return;
    }
    const url = URL.createObjectURL(image);
    setPreviewImage({ file: image, url });
    return () => URL.revokeObjectURL(url);
  }, [image]);
  const submit = handleSubmit(async (data) => {
    if (
      isSaving ||
      mode === 'view' ||
      (mode === 'edit' && !formState.isDirty)
    ) {
      return;
    }
    clearErrors('root.server');
    if (data.enabled && !readOnly) {
      try {
        // Refresh all pages immediately before saving; another user may have
        // scheduled an icon while this form was open.
        const icons = await availability.refetch().unwrap();
        if (data.isDefault) {
          const currentDefault = trackingIconEnabledDefault(
            icons,
            slot,
            icon?.id
          );
          if (currentDefault) {
            setError(
              'isDefault',
              {
                type: 'availability',
                message: `«${currentDefault.name}» ya es el ícono predeterminado habilitado. Editá ese ícono o guardá este deshabilitado.`,
              },
              { shouldFocus: true }
            );
            return;
          }
        } else {
          const errors = getDateRangeErrors(
            { from: data.dateFrom, to: data.dateTo },
            {
              withTime: true,
              allowOpenRange: true,
              blockedRanges: trackingIconBlockedRanges(icons, slot, icon?.id),
            }
          );
          if (errors.from || errors.to) {
            setError(
              errors.from ? 'dateFrom' : 'dateTo',
              {
                type: 'availability',
                message: errors.from || errors.to,
              },
              { shouldFocus: true }
            );
            return;
          }
        }
      } catch {
        setError('root.server', {
          message:
            'No pudimos comprobar la disponibilidad. Reintentá antes de guardar.',
        });
        return;
      }
    }
    const errorMessage = await action(data);
    if (errorMessage) setError('root.server', { message: errorMessage });
  });

  const imageUpload = (
    <FormFileInput
      name='image'
      control={control}
      accept='.png,.webp'
      placeholder='Arrastrá la imagen aquí'
    />
  );
  const nameInput = (
    <FormTextInput
      name='name'
      control={control}
      placeholder='Nombre *'
      disabled={readOnly}
    />
  );
  const positionSetting = (
    <div className='flex flex-col gap-2'>
      {readOnly ? (
        <div>
          <p className='text-sm text-gray-500'>Posición en el mapa</p>
          <p className='mt-1 font-medium'>
            {TRACKING_ICON_ANCHOR_LABELS[getTrackingIconAnchor(anchor)]}
          </p>
        </div>
      ) : (
        <FormSelectInput
          name='anchor'
          control={control}
          placeholder='Posición en el mapa'
          options={TRACKING_ICON_ANCHORS.map((value) => ({
            value,
            label: TRACKING_ICON_ANCHOR_LABELS[value],
          }))}
          required
        />
      )}
      {anchor && (
        <p className='text-sm text-gray-500'>
          {TRACKING_ICON_ANCHOR_DESCRIPTIONS[getTrackingIconAnchor(anchor)]}
        </p>
      )}
    </div>
  );
  const defaultSetting = (
    <div className='flex min-w-0 flex-col gap-2 rounded-xl border border-gray-200 bg-white p-4'>
      {readOnly ? (
        <div>
          <p className='text-sm text-gray-500'>Tipo de ícono</p>
          <p className='mt-1 font-medium'>
            {isDefault ? 'Predeterminado' : 'Fechas especiales'}
          </p>
        </div>
      ) : (
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <div className='order-last shrink-0'>
            <FormToggleSwitch
              name='isDefault'
              control={control}
              ariaLabel='Usar como predeterminado'
              onChange={(checked) => {
                if (checked) {
                  setValue('dateFrom', null, { shouldDirty: true });
                  setValue('dateTo', null, { shouldDirty: true });
                }
                clearErrors(['dateFrom', 'dateTo', 'isDefault']);
                clearErrors('root.server');
              }}
            />
          </div>
          <span className='text-sm font-medium text-gray-800'>
            Predeterminado
          </span>
        </div>
      )}
      <p
        id='tracking-icon-default-help'
        className='text-xs leading-relaxed text-gray-500'
      >
        {isDefault
          ? 'Se usa cuando no hay un ícono especial vigente.'
          : readOnly
            ? 'Reemplaza al predeterminado durante su vigencia.'
            : 'Cuando está desactivado, reemplaza al predeterminado durante su vigencia.'}
      </p>
      {formState.errors.isDefault?.message && (
        <p role='alert' className='text-sm text-red-600'>
          {formState.errors.isDefault.message}
        </p>
      )}
    </div>
  );
  const enabledSetting = (
    <fieldset
      disabled={readOnly}
      className='flex min-w-0 w-full flex-col gap-2 rounded-xl border border-gray-200 bg-white p-4 disabled:text-gray-400'
    >
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <p className='text-sm font-medium text-gray-800'>Habilitado</p>
        <div className='shrink-0'>
          <FormToggleSwitch
            control={control}
            name='enabled'
            ariaLabel='Habilitado'
          />
        </div>
      </div>
      <p className='text-xs leading-relaxed text-gray-500'>
        Puede mostrarse según su vigencia.
      </p>
    </fieldset>
  );

  return (
    <form
      className='flex min-w-0 w-full max-w-[1100px] mx-auto flex-col gap-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6 lg:p-8'
      onSubmit={submit}
    >
      <h2 className='text-2xl !font-bold select-none'>{title}</h2>
      <fieldset
        disabled={isSaving || formState.isSubmitting}
        className='grid min-w-0 items-start gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-8'
      >
        <section className='flex min-w-0 flex-col gap-5 rounded-xl bg-gray-50 p-3 sm:p-5'>
          <div className='flex items-center justify-between gap-3'>
            <div className='min-w-0'>
              <h3 className='text-sm font-semibold text-gray-800'>
                Imagen y posición
              </h3>
              {canChangeImage && (
                <p className='mt-1 text-xs text-gray-500'>
                  PNG o WebP, máximo 2 MB.
                </p>
              )}
            </div>
            {!isCreating && previewUrl && (
              <div className='shrink-0'>
                <TrackingIconImage
                  imageUrl={previewUrl}
                  name={watch('name') || 'Ícono seleccionado'}
                />
              </div>
            )}
          </div>
          {canChangeImage && (
            <div className='min-w-0 [&_.h-64]:h-36 [&_.h-64>svg]:text-4xl [&_.text-lg]:text-sm [&_p]:max-w-full [&_p]:break-words'>
              {imageUpload}
            </div>
          )}
          <div className='flex min-w-0 flex-col gap-3'>
            {positionSetting}
            <TrackingIconMapPreview
              slot={slot}
              imageUrl={previewUrl}
              name={watch('name') || 'Ícono seleccionado'}
              anchor={getTrackingIconAnchor(anchor)}
              canChangeImage={canChangeImage}
            />
          </div>
        </section>
        <div className='flex min-w-0 flex-1 flex-col gap-5'>
          {nameInput}
          <div className='grid min-w-0 gap-3 sm:grid-cols-2'>
            {defaultSetting}
            {enabledSetting}
          </div>
          {!isDefault && (
            <FormDateRangeInput
              control={control}
              fromName='dateFrom'
              toName='dateTo'
              placeholder='Vigencia'
              withTime
              allowOpenRange
              clearable
              blockedRanges={blockedRanges}
              disabled={readOnly || isSaving || formState.isSubmitting}
              helperText='Fechas y horas locales. Podés dejar un extremo sin límite.'
            />
          )}
          {!readOnly && (
            <div className='flex flex-col gap-3'>
              <div aria-live='polite' aria-atomic='true'>
                {!enabled ? (
                  <Alert severity='info'>
                    El ícono se guardará deshabilitado. Al habilitarlo se
                    comprobará su disponibilidad.
                  </Alert>
                ) : availabilityPending ? (
                  <p className='flex items-center gap-2 text-sm text-gray-500'>
                    <CircularProgress
                      size={16}
                      aria-label='Comprobando disponibilidad'
                    />{' '}
                    Comprobando disponibilidad…
                  </p>
                ) : availabilityFailed ? (
                  <Alert
                    severity='warning'
                    action={
                      <Button
                        color='inherit'
                        size='small'
                        onClick={() => void availability.refetch()}
                      >
                        Reintentar
                      </Button>
                    }
                  >
                    {availabilityMessage}
                  </Alert>
                ) : isDefault ? (
                  existingDefault ? (
                    <Alert severity='warning'>
                      {defaultConflictMessage}{' '}
                      <Link
                        className='underline'
                        to={`${trackingIconPath(slot)}/${existingDefault.id}`}
                      >
                        Ver predeterminado
                      </Link>
                      . Podés editar su imagen o guardar este ícono
                      deshabilitado.
                    </Alert>
                  ) : (
                    <p className='text-sm text-green-700'>
                      ✓ Disponible como predeterminado
                    </p>
                  )
                ) : isInitialEmptyPeriod ? (
                  <p className='text-sm text-gray-500'>
                    Elegí un período. Podés consultar las fechas ocupadas abajo.
                  </p>
                ) : !validPeriod ? (
                  <p className='text-sm text-gray-500'>
                    Ingresá un período válido para comprobar su disponibilidad.
                  </p>
                ) : conflicts.length > 0 ? (
                  <Alert severity='warning'>
                    {dateFrom || dateTo
                      ? 'El período coincide con otros íconos habilitados.'
                      : 'Sin inicio ni fin, la vigencia abarca también los períodos ocupados.'}{' '}
                    Elegí fechas disponibles o guardá el ícono deshabilitado.
                  </Alert>
                ) : (
                  <p className='text-sm text-green-700'>✓ Período disponible</p>
                )}
              </div>
              {!isDefault &&
                !availabilityFailed &&
                parsedAvailability.ranges.length > 0 && (
                  <details
                    className='rounded-md border border-gray-200 p-3'
                    open={conflicts.length > 0 && !!(dateFrom || dateTo)}
                  >
                    <summary className='cursor-pointer text-sm font-medium text-gray-700'>
                      {conflicts.length > 0 && (dateFrom || dateTo)
                        ? `Íconos que coinciden (${conflicts.length})`
                        : `Períodos ocupados (${parsedAvailability.ranges.length})`}
                    </summary>
                    <ul className='mt-3 flex max-h-60 flex-col gap-3 overflow-y-auto text-sm'>
                      {(conflicts.length > 0 && (dateFrom || dateTo)
                        ? conflicts
                        : parsedAvailability.ranges
                      ).map((range) => (
                        <li
                          key={range.id}
                          className='flex flex-wrap items-start justify-between gap-2 border-t border-gray-100 pt-3'
                        >
                          <div>
                            <p className='font-medium text-gray-800'>
                              {range.label}
                            </p>
                            <p className='text-gray-500'>
                              {range.from
                                ? formatDate(range.from.toISOString())
                                : 'Sin inicio'}{' '}
                              —{' '}
                              {range.to
                                ? formatDate(range.to.toISOString())
                                : 'Sin fin'}
                            </p>
                          </div>
                          <Link
                            className='text-[var(--primary-color)] underline'
                            to={`${trackingIconPath(slot)}/${range.id}`}
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            Ver ícono (nueva pestaña)
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
            </div>
          )}
          {mode === 'view' && icon && (
            <dl className='grid gap-4 border-t border-gray-200 pt-5 text-sm sm:grid-cols-2'>
              <div className='min-w-0'>
                <dt className='text-gray-500'>Creado por</dt>
                <dd className='mt-1 break-words'>
                  {icon.createdByName || 'Sin registro'}
                </dd>
                <dd className='mt-1 text-xs text-gray-500'>
                  <time
                    dateTime={icon.createdAt}
                    title={formatDate(icon.createdAt)}
                  >
                    {timeAgo(icon.createdAt)}
                  </time>
                </dd>
              </div>
              <div className='min-w-0'>
                <dt className='text-gray-500'>Última modificación por</dt>
                <dd className='mt-1 break-words'>
                  {icon.updatedByName || 'Sin registro'}
                </dd>
                <dd className='mt-1 text-xs text-gray-500'>
                  <time
                    dateTime={icon.updatedAt}
                    title={formatDate(icon.updatedAt)}
                  >
                    {timeAgo(icon.updatedAt)}
                  </time>
                </dd>
              </div>
            </dl>
          )}
        </div>
      </fieldset>
      {formState.errors.root?.server?.message && (
        <Alert severity='error'>{formState.errors.root.server.message}</Alert>
      )}
      {mode !== 'view' && (
        <div className='flex flex-col-reverse gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:justify-end'>
          {renderButtons(submit, formState)}
        </div>
      )}
    </form>
  );
};

export default TrackingIconForm;
